import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createCertification, updateCertification } from "app/services/skill.service";
import { loadMembersForAllClientsFromBackend } from "app/services/member.service";

const initialData = {
  id: null,
  memberId: "",
  certificationName: "",
  issuingOrganization: "",
  issueDate: "",
  expiryDate: "",
  credentialId: "",
  credentialUrl: "",
  doesNotExpire: false,
  notes: "",
};

const AddCertificationDialog = ({ open, setOpen, onSuccess, editObject, memberId }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Load members when dialog opens
  useEffect(() => {
    if (open && members.length === 0) {
      setLoadingMembers(true);
      loadMembersForAllClientsFromBackend()
        .then((response) => {
          setMembers(response.data || []);
        })
        .catch((error) => {
          console.error("Failed to load members:", error);
          setMembers([]);
        })
        .finally(() => setLoadingMembers(false));
    }
  }, [open, members.length]);

  useEffect(() => {
    if (editObject) {
      setData({
        id: editObject.id,
        memberId: editObject.memberId || memberId || "",
        certificationName: editObject.certificationName || "",
        issuingOrganization: editObject.issuingOrganization || "",
        issueDate: editObject.issueDate?.substring(0, 10) || "",
        expiryDate: editObject.expiryDate?.substring(0, 10) || "",
        credentialId: editObject.credentialId || "",
        credentialUrl: editObject.credentialUrl || "",
        doesNotExpire: !editObject.expiryDate,
        notes: editObject.notes || "",
      });
    } else {
      setData({ ...initialData, memberId: memberId || "" });
    }
    setMessageOpen(false);
  }, [editObject, open, memberId]);

  const validateForm = () => {
    if (!data.memberId) {
      setError("Please select a member");
      return false;
    }
    if (!data.certificationName || data.certificationName.trim() === "") {
      setError("Certification name is required");
      return false;
    }
    if (!data.issuingOrganization || data.issuingOrganization.trim() === "") {
      setError("Issuing organization is required");
      return false;
    }
    if (!data.issueDate) {
      setError("Issue date is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const selectedMemberId = memberId || data.memberId;
      const payload = {
        ...data,
        memberId: Number(selectedMemberId), // Ensure memberId is sent as a number
        expiryDate: data.doesNotExpire ? null : (data.expiryDate || null),
      };
      // Remove unnecessary fields
      delete payload.doesNotExpire;
      delete payload.notes;
      if (!payload.notes) delete payload.notes;

      if (editObject) {
        await updateCertification(data.id, payload);
        setSuccess("Certification updated successfully");
      } else {
        await createCertification(payload);
        setSuccess("Certification added successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Certification" : "Add Certification"}</DialogTitle>
      <DialogContent>
        {/* Member Selection - show only if memberId is not pre-set */}
        {!memberId && (
          <MDBox my={2}>
            <TextField
              id="memberId"
              label="Select Member"
              fullWidth
              select
              variant="outlined"
              value={data.memberId}
              onChange={(e) => setData({ ...data, memberId: e.target.value })}
              required
              helperText="*Required"
              disabled={loadingMembers}
              InputProps={{
                endAdornment: loadingMembers ? <CircularProgress size={20} /> : null,
              }}
            >
              {members.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name} ({member.clientEmail})
                </MenuItem>
              ))}
            </TextField>
          </MDBox>
        )}
        <MDBox my={2}>
          <TextField
            id="certificationName"
            label="Certification Name"
            fullWidth
            variant="outlined"
            value={data.certificationName}
            onChange={(e) => setData({ ...data, certificationName: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="issuingOrganization"
            label="Issuing Organization"
            fullWidth
            variant="outlined"
            value={data.issuingOrganization}
            onChange={(e) => setData({ ...data, issuingOrganization: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="issueDate"
            label="Issue Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.issueDate}
            onChange={(e) => setData({ ...data, issueDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            id="expiryDate"
            label="Expiry Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.expiryDate}
            onChange={(e) => setData({ ...data, expiryDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            disabled={data.doesNotExpire}
          />
        </MDBox>
        <MDBox my={2}>
          <FormControlLabel
            control={
              <Switch
                checked={data.doesNotExpire}
                onChange={(e) => setData({ ...data, doesNotExpire: e.target.checked, expiryDate: "" })}
              />
            }
            label="Does Not Expire"
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="credentialId"
            label="Credential ID"
            fullWidth
            variant="outlined"
            value={data.credentialId}
            onChange={(e) => setData({ ...data, credentialId: e.target.value })}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="credentialUrl"
            label="Credential URL"
            fullWidth
            variant="outlined"
            value={data.credentialUrl}
            onChange={(e) => setData({ ...data, credentialUrl: e.target.value })}
            placeholder="https://..."
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="notes"
            label="Notes"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.notes}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddCertificationDialog);

